/* KOJO · header module: <kj-header> (scroll states, cart badge), <kj-menu> (full-screen menu), <kj-search> (search overlay + predictive search).
   Depends on assets/kojo-core.js (window.KJ). Vanilla, no vendor globals. */
(function () {
  'use strict';
  var doc = document;
  var root = doc.documentElement;
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function visible(el) { return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length); }
  function scrollY() { return window.pageYOffset || root.scrollTop || 0; }

  function start() {
    var KJ = window.KJ;
    // boot once (double inclusion / late call): global listeners below must never be attached twice
    if (!KJ || !KJ.DrawerBase || KJ._hdrBooted) return;
    KJ._hdrBooted = true;
    var reduced = function () { return KJ.reducedMotion ? KJ.reducedMotion() : false; };
    // each element is guarded on its own so a name defined elsewhere never aborts the rest of start()
    function define(name, ctor) {
      if (customElements.get(name)) return;
      try { customElements.define(name, ctor); } catch (err) {}
    }

    /* ---------- <kj-header> ---------- */
    define('kj-header', class extends HTMLElement {
      connectedCallback() {
        if (this._ready) return;
        this._ready = true;
        this.ticker = this.querySelector('[data-kj-ticker]');
        this.hideOnScroll = this.dataset.hideOnScroll !== 'false';
        this.state = { scrolled: false, compact: false, hidden: false, menu: false };
        this.lastY = scrollY();
        this.acc = 0;
        this._raf = 0;
        this._count = this.countValue();
        var self = this;
        this._onScroll = function () {
          if (self._raf) return;
          self._raf = requestAnimationFrame(function () { self._raf = 0; self.update(); });
        };
        this._onResize = KJ.debounce(function () { self.measure(); }, 120);
        this._onDrawer = function (e) {
          var open = e.type === 'kj:drawer:open';
          if (open) self.setState({ hidden: false });
          if (e.detail && e.detail.name === 'menu') self.setState({ menu: open });
        };
        this._onCart = function () { self.bump(); };
        this._onFocus = function () { self.setState({ hidden: false }); };
        window.addEventListener('scroll', this._onScroll, { passive: true });
        window.addEventListener('resize', this._onResize, { passive: true });
        doc.addEventListener('kj:drawer:open', this._onDrawer);
        doc.addEventListener('kj:drawer:close', this._onDrawer);
        doc.addEventListener('kj:cart:updated', this._onCart);
        this.addEventListener('focusin', this._onFocus);
        if ('ResizeObserver' in window && this.ticker) {
          this._ro = new ResizeObserver(this._onResize);
          this._ro.observe(this.ticker);
        }
        this.measure();
        this.update();
      }
      disconnectedCallback() {
        this._ready = false;
        window.removeEventListener('scroll', this._onScroll);
        window.removeEventListener('resize', this._onResize);
        doc.removeEventListener('kj:drawer:open', this._onDrawer);
        doc.removeEventListener('kj:drawer:close', this._onDrawer);
        doc.removeEventListener('kj:cart:updated', this._onCart);
        this.removeEventListener('focusin', this._onFocus);
        if (this._ro) this._ro.disconnect();
        if (this._raf) cancelAnimationFrame(this._raf);
        root.classList.remove('kj-header--hidden', 'kj-header--compact');
      }
      countValue() {
        var el = this.querySelector('[data-kj-cart-count]');
        return el ? parseInt(el.getAttribute('data-count') || el.textContent, 10) || 0 : 0;
      }
      bump() {
        var el = this.querySelector('[data-kj-cart-count]');
        var n = this.countValue();
        if (el && n > this._count && !reduced()) {
          el.classList.remove('is-bump');
          void el.offsetWidth;
          el.classList.add('is-bump');
          setTimeout(function () { el.classList.remove('is-bump'); }, 600);
        }
        this._count = n;
      }
      measure() {
        this.tickH = this.ticker ? this.ticker.offsetHeight : 0;
        this.style.setProperty('--kj-tick-h', this.tickH + 'px');
        this.h = Math.round(this.getBoundingClientRect().height);
        this.publish();
      }
      update() {
        var y = Math.max(0, scrollY());
        var dy = y - this.lastY;
        this.lastY = y;
        var next = { scrolled: y > 4, compact: y > 60, hidden: this.state.hidden };
        var locked = root.classList.contains('kj-locked') || this.state.menu;
        if (locked) {
          next.hidden = false;
          this.acc = 0;
        } else if (!this.hideOnScroll || y <= 320) {
          next.hidden = false;
          this.acc = 0;
        } else if (dy !== 0) {
          if ((dy > 0) !== (this.acc > 0)) this.acc = 0;
          this.acc += dy;
          if (this.acc > 10) next.hidden = true;
          else if (this.acc < -10) next.hidden = false;
        }
        var ae = doc.activeElement;
        if (next.hidden && ae && this.contains(ae)) { try { if (ae.matches(':focus-visible')) next.hidden = false; } catch (err) {} }
        this.setState(next);
      }
      setState(patch) {
        var s = this.state, changed = false;
        Object.keys(patch).forEach(function (k) { if (s[k] !== patch[k]) { s[k] = patch[k]; changed = true; } });
        if (!changed) return;
        if (s.menu) s.hidden = false;
        this.classList.toggle('kj-hdr--scrolled', s.scrolled);
        this.classList.toggle('kj-hdr--compact', s.compact && !!this.ticker);
        this.classList.toggle('kj-hdr--hidden', s.hidden);
        this.classList.toggle('kj-hdr--menu', s.menu);
        root.classList.toggle('kj-header--compact', s.compact);
        root.classList.toggle('kj-header--hidden', s.hidden);
        this.publish();
      }
      publish() {
        var s = this.state;
        var vis = s.hidden ? 0 : (s.compact ? (this.h || 0) - (this.tickH || 0) : (this.h || 0));
        root.style.setProperty('--kj-header-visible', Math.max(0, vis) + 'px');
      }
    });

    /* ---------- <kj-menu> : full-screen menu below the pill ---------- */
    define('kj-menu', class extends KJ.DrawerBase(HTMLElement) {
      connectedCallback() {
        super.connectedCallback();
        if (this._menuReady) return;
        this._menuReady = true;
        var self = this;
        this.panel = this.querySelector('[role="dialog"]') || this;
        this.aside = this.querySelector('.kj-menu__aside');
        this.cap = this.querySelector('[data-kj-pv-cap]');
        this.pvs = {};
        KJ.qsa('[data-kj-pv-img]', this).forEach(function (el) { self.pvs[el.getAttribute('data-kj-pv-img')] = el; });
        var onIntent = function (e) {
          var a = e.target.closest && e.target.closest('[data-kj-pv]');
          if (a && self.contains(a)) self.preview(a.getAttribute('data-kj-pv'), a.getAttribute('data-kj-pv-label'));
        };
        this.addEventListener('pointerover', onIntent);
        this.addEventListener('focusin', onIntent);
        var nav = this.querySelector('[data-kj-menu-nav]');
        if (nav) nav.addEventListener('pointerleave', function () { self.preview('default'); });
        this.addEventListener('click', function (e) {
          var a = e.target.closest('a[href]');
          if (!a || !self.contains(a)) return;
          var href = a.getAttribute('href') || '';
          if (href.charAt(0) === '#' || (a.pathname === location.pathname && a.hash)) self.close();
        });
        this.markCurrent();
      }
      markCurrent() {
        var path = location.pathname.replace(/\/$/, '') || '/';
        KJ.qsa('a.kj-menu__link, a.kj-menu__vol', this).forEach(function (a) {
          var p = (a.pathname || '').replace(/\/$/, '') || '/';
          if (a.host === location.host && p === path && p !== '/') a.setAttribute('aria-current', 'page');
        });
      }
      /* priming only reveals the default preview; item previews keep data-srcset/data-src until first hover/focus */
      prime() { this.classList.add('is-primed'); }
      asideVisible() { return !!(this.aside && this.aside.offsetWidth); }
      load(el) {
        var img = el.querySelector('img[data-srcset], img[data-src]');
        if (!img) return null;
        var ss = img.getAttribute('data-srcset'), src = img.getAttribute('data-src');
        img.removeAttribute('data-srcset');
        img.removeAttribute('data-src');
        img.loading = 'eager';
        if (ss) img.setAttribute('srcset', ss);
        if (src) img.setAttribute('src', src);
        return img;
      }
      preview(id, label) {
        if (!id) return;
        var el = this.pvs[id];
        if (!el) return;
        if (this._pv === id) return;
        if (!this.asideVisible()) return; // mobile/tablet: preview column hidden → never fetch preview images
        this._pv = id;
        var self = this;
        var img = this.load(el) || el.querySelector('img');
        var show = function () {
          if (self._pv !== id) return;
          Object.keys(self.pvs).forEach(function (k) { self.pvs[k].classList.toggle('is-active', k === id); });
          if (self.cap) self.cap.textContent = id === 'default' ? (self.cap.getAttribute('data-default') || '') : (label || '');
        };
        // keep the current image until the new one is decoded (no black flash while it downloads)
        if (img && !img.complete) {
          var done = function () { img.removeEventListener('load', done); img.removeEventListener('error', done); show(); };
          img.addEventListener('load', done);
          img.addEventListener('error', done);
          setTimeout(done, 1500);
        } else show();
      }
      header() { return doc.querySelector('kj-header'); }
      open(opener) {
        if (this.isOpen) return;
        this.prime();
        this.preview('default');
        super.open(opener);
        if (this._release) this._release();
        this._release = this.trap();
        var self = this;
        this._esc = function (e) { if (e.key === 'Escape' && self.isOpen) { e.preventDefault(); self.close(); } };
        doc.addEventListener('keydown', this._esc);
        if (this.panel) this.panel.scrollTop = 0;
      }
      close() {
        if (!this.isOpen) return;
        super.close();
        if (this._esc) doc.removeEventListener('keydown', this._esc);
      }
      /* focus trap spanning the menu panel AND the header pill (burger = close control) */
      trap() {
        var self = this;
        function items() {
          var bar = doc.querySelector('kj-header .kj-hdr__bar');
          var list = KJ.qsa(FOCUSABLE, self.panel);
          if (bar) list = list.concat(KJ.qsa(FOCUSABLE, bar));
          return list.filter(visible);
        }
        function onKey(e) {
          if (e.key !== 'Tab' || !self.isOpen) return;
          var top = doc.querySelector('kj-search.is-open, kj-cart-drawer.is-open, kj-drawer.is-open');
          if (top) return;
          var els = items();
          if (!els.length) return;
          var idx = els.indexOf(doc.activeElement);
          var next = idx === -1 ? (e.shiftKey ? els.length - 1 : 0) : (idx + (e.shiftKey ? -1 : 1) + els.length) % els.length;
          e.preventDefault();
          els[next].focus();
        }
        doc.addEventListener('keydown', onKey);
        return function () { doc.removeEventListener('keydown', onKey); };
      }
    });

    /* ---------- <kj-search> : overlay from top + predictive search ---------- */
    define('kj-search', class extends KJ.DrawerBase(HTMLElement) {
      connectedCallback() {
        super.connectedCallback();
        if (this._sReady) return;
        this._sReady = true;
        var self = this;
        this.input = this.querySelector('[data-kj-search-input]');
        this.form = this.querySelector('[data-kj-search-form]');
        this.results = this.querySelector('[data-kj-search-results]');
        this.idle = this.querySelector('[data-kj-search-idle]');
        this.clearBtn = this.querySelector('[data-kj-search-clear]');
        this.status = this.querySelector('[data-kj-search-status]');
        this.cache = {};
        this.go = {}; // q → collection URL when q is the start of a suggested collection title ('' = none)
        if (!this.input) return;
        this._run = KJ.debounce(function () { self.run(); }, 220);
        this.input.addEventListener('input', function () { self.sync(); self._run(); });
        if (this.clearBtn) this.clearBtn.addEventListener('click', function () { self.clear(); self.input.focus(); });
        if (this.form) this.form.addEventListener('submit', function (e) { self.submit(e); });
        this._onVV = function () { self.fitViewport(); };
        this.addEventListener('click', function (e) {
          var chip = e.target.closest('[data-kj-term]');
          if (!chip || !self.contains(chip)) return;
          e.preventDefault();
          self.input.value = chip.getAttribute('data-kj-term');
          self.sync();
          self.run();
          try { self.input.focus({ preventScroll: true }); } catch (err) {}
        });
        this.sync();
      }
      open(opener) {
        if (this.isOpen) return;
        this.fill();
        super.open(opener);
        var vv = window.visualViewport;
        if (vv) { vv.addEventListener('resize', this._onVV); this.fitViewport(); }
        if (this.input) {
          try { this.input.focus({ preventScroll: true }); } catch (e) {}
          if (this.input.value) { try { this.input.select(); } catch (e) {} this.run(); }
        }
      }
      close() {
        if (!this.isOpen) return;
        super.close();
        var vv = window.visualViewport;
        if (vv) vv.removeEventListener('resize', this._onVV);
      }
      /* on-screen keyboard: cap the panel to the visible viewport so the sticky "Ver todos" stays above it */
      fitViewport() {
        var vv = window.visualViewport;
        if (vv && vv.height) this.style.setProperty('--kj-vvh', Math.round(vv.height) + 'px');
      }
      /* Enter: when the query starts a collection title (anil → Anillos) go to that collection,
         because the full /search only matches whole words; otherwise normal GET /search?q=…&type=product */
      submit(e) {
        var self = this;
        var q = this.input.value.trim();
        if (!q) { e.preventDefault(); this.input.focus(); return; }
        if (this._going) { e.preventDefault(); return; }
        if (q.length < 2 || this.go[q] === '') return;
        e.preventDefault();
        if (this.go[q]) { location.assign(this.go[q]); return; }
        this._going = true;
        var done = false, timer = 0;
        var fin = function () {
          if (done) return;
          done = true;
          clearTimeout(timer);
          self._going = false;
          if (self.go[q]) location.assign(self.go[q]);
          else if (self.form) self.form.submit();
        };
        timer = setTimeout(fin, 1500);
        var p = this.run();
        if (p && p.then) p.then(fin, fin); else fin();
      }
      fill() {
        if (this._filled) return;
        this._filled = true;
        var tpl = this.querySelector('template[data-kj-search-tpl]');
        var host = this.querySelector('[data-kj-search-suggest]');
        if (tpl && host) { host.appendChild(tpl.content.cloneNode(true)); if (KJ.init) KJ.init(host); }
      }
      clear() {
        this.input.value = '';
        this.sync();
        this.run();
      }
      sync() {
        if (this.clearBtn) this.clearBtn.hidden = !this.input.value.length;
      }
      setStatus(t) { if (this.status) this.status.textContent = t; }
      showIdle() {
        if (this.ctrl) { this.ctrl.abort(); this.ctrl = null; }
        this.classList.remove('is-loading');
        this.results.innerHTML = '';
        this.results.hidden = true;
        if (this.idle) this.idle.hidden = false;
      }
      show(html, q) {
        this.results.innerHTML = html;
        this.results.hidden = false;
        var box = this.results.querySelector('[data-kj-ps]');
        var n = box ? parseInt(box.getAttribute('data-total'), 10) || 0 : 0;
        if (this.idle) this.idle.hidden = n > 0;
        this.setStatus(n ? n + ' resultados para ' + q : 'Sin resultados para ' + q);
      }
      run() {
        var self = this;
        var q = this.input.value.trim();
        if (q.length < 2) { this.showIdle(); this.setStatus(''); return null; }
        if (this.cache[q] !== undefined) { this.show(this.cache[q], q); return Promise.resolve(); }
        if (this.pending && this.pendingQ === q) return this.pending;
        if (this.ctrl) this.ctrl.abort();
        var ctrl = this.ctrl = ('AbortController' in window) ? new AbortController() : null;
        this.classList.add('is-loading');
        var base = (KJ.routes && KJ.routes.predictive) || '/search/suggest';
        var url = base + '?q=' + encodeURIComponent(q) +
          '&resources%5Btype%5D=product%2Ccollection%2Cpage&resources%5Blimit%5D=6&resources%5Blimit_scope%5D=each&section_id=kojo-predictive-search';
        this.pendingQ = q;
        var p = this.pending = fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
          .then(function (html) {
            var el = KJ.parseHTML(html).querySelector('[data-kj-ps]');
            var out = el ? el.outerHTML : '';
            self.cache[q] = out;
            self.go[q] = el ? (el.getAttribute('data-kj-ps-go') || '') : '';
            if (self.input.value.trim() === q) self.show(out, q);
          })
          .catch(function (err) {
            if (err && err.name === 'AbortError') return;
            var safe = q.replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
            var href = (self.form ? self.form.getAttribute('action') : '/search') + '?q=' + encodeURIComponent(q) + '&type=product';
            if (self.input.value.trim() === q) self.show('<div class="kj-ps" data-kj-ps data-total="0"><div class="kj-ps__foot"><a class="kj-btn kj-btn--block kj-ps__all" href="' + href + '">Ver resultados de «' + safe + '»</a></div></div>', q);
          })
          .then(function () {
            if (self.ctrl === ctrl) { self.classList.remove('is-loading'); self.ctrl = null; }
            if (self.pending === p) { self.pending = null; self.pendingQ = null; }
          });
        return p;
      }
    });

    /* ---------- global delegation ---------- */
    function menuEl() { return doc.querySelector('kj-menu'); }
    function searchEl() { return doc.querySelector('kj-search'); }

    doc.addEventListener('click', function (e) {
      var t = e.target.closest && e.target.closest('[data-kj-menu-toggle]');
      if (t) {
        var m = doc.getElementById(t.getAttribute('aria-controls')) || menuEl();
        if (!m || typeof m.open !== 'function') return;
        e.preventDefault();
        if (m.isOpen) m.close(); else m.open(t);
        return;
      }
      var s = e.target.closest && e.target.closest('[data-kj-search-open]');
      if (s) {
        var srch = searchEl();
        if (!srch || typeof srch.open !== 'function') return;
        e.preventDefault();
        var opener = s;
        var menu = menuEl();
        if (menu && menu.isOpen && menu.contains(s)) {
          menu.close();
          opener = doc.querySelector('[data-kj-menu-toggle]') || s;
        }
        srch.open(opener);
      }
    });
    function primeMenu(e) {
      var t = e.target.closest && e.target.closest('[data-kj-menu-toggle]');
      if (t) { var m = menuEl(); if (m && m.prime) m.prime(); }
    }
    doc.addEventListener('pointerover', primeMenu, { passive: true });
    doc.addEventListener('touchstart', primeMenu, { passive: true });
    doc.addEventListener('focusin', primeMenu);

    // another overlay (cart, quick add, search) takes over → close the menu
    doc.addEventListener('kj:drawer:open', function (e) {
      var name = e.detail && e.detail.name;
      if (name === 'menu') return;
      var m = menuEl();
      if (m && m.isOpen) m.close();
    });

    // "/" opens search (desktop keyboard users)
    doc.addEventListener('keydown', function (e) {
      if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
      var a = doc.activeElement;
      if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return;
      var s = searchEl();
      if (s && !s.isOpen && !root.classList.contains('kj-locked')) { e.preventDefault(); s.open(a); }
    });

    // localization select (menu utility row only) submits on change; other modules handle their own forms
    doc.addEventListener('change', function (e) {
      var sel = e.target.closest && e.target.closest('kj-menu [data-kj-autosubmit]');
      if (!sel || !sel.form) return;
      var rt = sel.form.querySelector('[name="return_to"]');
      if (rt) rt.value = location.pathname + location.search; // keep ?variant= / filters
      sel.form.submit();
    });

    // back/forward cache: never restore with overlays open
    window.addEventListener('pageshow', function (e) {
      if (!e.persisted) return;
      var m = menuEl(), s = searchEl();
      if (m && m.isOpen) m.close();
      if (s && s.isOpen) s.close();
    });

    // theme editor
    function menuFor(sectionId) { return doc.querySelector('kj-menu[data-section-id="' + sectionId + '"]'); }
    doc.addEventListener('shopify:section:select', function (e) { var m = menuFor(e.detail.sectionId); if (m) m.open(); });
    doc.addEventListener('shopify:section:deselect', function (e) { var m = menuFor(e.detail.sectionId); if (m) m.close(); });
    doc.addEventListener('shopify:block:select', function (e) {
      var m = menuFor(e.detail.sectionId);
      if (!m) return;
      var blk = m.querySelector('[data-kj-block="' + e.detail.blockId + '"]');
      if (!blk) return;
      m.open();
      setTimeout(function () { try { blk.scrollIntoView({ block: 'center' }); } catch (err) {} }, 80);
    });
    doc.addEventListener('shopify:section:unload', function (e) {
      var m = menuFor(e.detail.sectionId);
      if (m && m.isOpen) m.close();
    });
  }

  if (window.KJ && window.KJ.DrawerBase) start();
  else doc.addEventListener('DOMContentLoaded', start, { once: true });
})();
